// submission.ts
import { isEmpty } from 'src/helpers/validation-utils';
import { WidgetType } from '.';
import { IPageData } from './page';
import { validateSeoField, validateRepeatorFieldWithBoolean } from './validation';

/**
 * Helper: Recursively checks if a nested error object has any non-empty error message.
 * (An empty string ("") means no error.)
 */
const hasNestedErrors = (value: any): boolean => {
  if (typeof value === 'string') return value !== '';
  if (Array.isArray(value)) return value.some(hasNestedErrors);
  if (value !== null && typeof value === 'object') {
    return Object.values(value).some(hasNestedErrors);
  }
  return false;
};

export const validateForm = (pageData: IPageData) => {
  const seoErrors = {
    Title: validateSeoField('Title', pageData.SEO.Title),
    Description: validateSeoField('Description', pageData.SEO.Description),
    Keywords: validateSeoField('Keywords', pageData.SEO.Keywords),
    Code: validateSeoField('Code', pageData.SEO.Code),
  };

  let isValidItems = true;

  // Validate each widget in the Items array.
  const itemsErrors = pageData.Items.map((item: any) => {
    switch (item.type) {
      case WidgetType.BannerV1:
        return {
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((subItem: any) => ({
                Title: validateRepeatorFieldWithBoolean('Title', item, subItem.Title, isValidItems),
                Subtitle: validateRepeatorFieldWithBoolean('Subtitle', item, subItem.Subtitle, isValidItems),
                ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, subItem.ImageUrl, isValidItems)
              }))
            : "At least one slide is required"
        };

      case WidgetType.BannerV2:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((subItem: any) => ({
                LinkTitle: validateRepeatorFieldWithBoolean('LinkTitle', item, subItem.LinkTitle, isValidItems),
                Link: validateRepeatorFieldWithBoolean('Link', item, subItem.Link, isValidItems)
              }))
            : "At least one navigation link is required"
        };

      case WidgetType.WhoWeAreV1:
        return {
          Items: (Array.isArray(item.Items) && item.Items.length === 2)
            ? {
                Item1: {
                  Title: validateRepeatorFieldWithBoolean('Title', item, item.Items[0]?.Title, isValidItems),
                  Description: validateRepeatorFieldWithBoolean('Description', item, item.Items[0]?.Description, isValidItems)
                },
                Item2: {
                  Description: validateRepeatorFieldWithBoolean('Description', item, item.Items[1]?.Description, isValidItems),
                  LinkTitle: validateRepeatorFieldWithBoolean('LinkTitle', item, item.Items[1]?.LinkTitle, isValidItems),
                  Link: validateRepeatorFieldWithBoolean('Link', item, item.Items[1]?.Link, isValidItems)
                }
              }
            : "Exactly two sections are required"
        };

      case WidgetType.ServicesV1:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Description: validateRepeatorFieldWithBoolean('Description', item, item.Description, isValidItems),
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((subItem: any) => ({
                Title: validateRepeatorFieldWithBoolean('Title', item, subItem.Title, isValidItems),
                ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, subItem.ImageUrl, isValidItems),
                Description: validateRepeatorFieldWithBoolean('Description', item, subItem.Description, isValidItems),
                LinkTitle: validateRepeatorFieldWithBoolean('LinkTitle', item, subItem.LinkTitle, isValidItems),
                Link: validateRepeatorFieldWithBoolean('Link', item, subItem.Link, isValidItems)
              }))
            : "At least one service is required"
        };

      case WidgetType.ServicePage:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((subItem: any) => ({
                Title: validateRepeatorFieldWithBoolean('Title', item, subItem.Title, isValidItems),
                ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, subItem.ImageUrl, isValidItems),
                Description: validateRepeatorFieldWithBoolean('Description', item, subItem.Description, isValidItems),
                LinkTitle: validateRepeatorFieldWithBoolean('LinkTitle', item, subItem.LinkTitle, isValidItems),
                Link: validateRepeatorFieldWithBoolean('Link', item, subItem.Link, isValidItems)
              }))
            : "At least one service page item is required"
        };

      case WidgetType.ServiceDetails:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Header: validateRepeatorFieldWithBoolean('Header', item, item.Header, isValidItems),
          Description: validateRepeatorFieldWithBoolean('Description', item, item.Description, isValidItems),
          ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, item.ImageUrl, isValidItems),
          Properties: {
            ExtraParagraph: validateRepeatorFieldWithBoolean('ExtraParagraph', item, item.Properties?.ExtraParagraph, isValidItems),
            Accordion: (Array.isArray(item.Properties?.Accordion) && item.Properties.Accordion.length > 0)
              ? item.Properties.Accordion.map((subItem: any) => ({
                  Question: validateRepeatorFieldWithBoolean('Question', item, subItem.Question, isValidItems),
                  Answer: validateRepeatorFieldWithBoolean('Answer', item, subItem.Answer, isValidItems)
                }))
              : "At least one accordion entry is required",
            Steps: (Array.isArray(item.Properties?.Steps) && item.Properties.Steps.length > 0)
              ? item.Properties.Steps.map((subItem: any) => ({
                  Step: validateRepeatorFieldWithBoolean('Step', item, subItem.Step, isValidItems),
                  Title: validateRepeatorFieldWithBoolean('Title', item, subItem.Title, isValidItems),
                  Text: validateRepeatorFieldWithBoolean('Text', item, subItem.Text, isValidItems)
                }))
              : "At least one step is required"
          }
        };

      case WidgetType.ProjectsV1:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Description: validateRepeatorFieldWithBoolean('Description', item, item.Description, isValidItems),
          Subtitle: validateRepeatorFieldWithBoolean('Subtitle', item, item.Subtitle, isValidItems),
          LinkTitle: validateRepeatorFieldWithBoolean('LinkTitle', item, item.LinkTitle, isValidItems),
          Link: validateRepeatorFieldWithBoolean('Link', item, item.Link, isValidItems),
          Images: (Array.isArray(item.Images) && item.Images.length === 7)
            ? ''
            : 'Exactly 7 images are required'
        };

      case WidgetType.ProjectsListing:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Filters: (Array.isArray(item.Filters) && item.Filters.length > 0)
            ? item.Filters.map((filter: any) => ({
                Title: validateRepeatorFieldWithBoolean('Title', item, filter.Title, isValidItems),
                Properties: {
                  Filter: validateRepeatorFieldWithBoolean('Filter', item, filter.Properties?.Filter, isValidItems)
                }
              }))
            : "At least one filter is required",
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((proj: any) => ({
                Title: validateRepeatorFieldWithBoolean('Title', item, proj.Title, isValidItems),
                Subtitle: validateRepeatorFieldWithBoolean('Subtitle', item, proj.Subtitle, isValidItems),
                Category: validateRepeatorFieldWithBoolean('Category', item, proj.Category, isValidItems),
                ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, proj.ImageUrl, isValidItems),
                Link: validateRepeatorFieldWithBoolean('Link', item, proj.Link, isValidItems)
              }))
            : "At least one project is required"
        };

      case WidgetType.Contact:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Header: validateRepeatorFieldWithBoolean('Header', item, item.Header, isValidItems),
          Description: validateRepeatorFieldWithBoolean('Description', item, item.Description, isValidItems),
          Properties: {
            CallHeading: validateRepeatorFieldWithBoolean('CallHeading', item, item.Properties?.CallHeading, isValidItems),
            Phone: validateRepeatorFieldWithBoolean('Phone', item, item.Properties?.Phone, isValidItems),
            EmailHeading: validateRepeatorFieldWithBoolean('EmailHeading', item, item.Properties?.EmailHeading, isValidItems),
            Email: validateRepeatorFieldWithBoolean('Email', item, item.Properties?.Email, isValidItems),
            LocationHeading: validateRepeatorFieldWithBoolean('LocationHeading', item, item.Properties?.LocationHeading, isValidItems),
            Location: validateRepeatorFieldWithBoolean('Location', item, item.Properties?.Location, isValidItems),
            CorporateHeading: validateRepeatorFieldWithBoolean('CorporateHeading', item, item.Properties?.CorporateHeading, isValidItems),
            CorporateDescription: validateRepeatorFieldWithBoolean('CorporateDescription', item, item.Properties?.CorporateDescription, isValidItems),
            MapUrl: validateRepeatorFieldWithBoolean('MapUrl', item, item.Properties?.MapUrl, isValidItems)
          }
        };

      case WidgetType.CompanyDetails:
        return {
          Items: (Array.isArray(item.Items) && item.Items.length > 0)
            ? item.Items.map((comp: any) => ({
                Header: validateRepeatorFieldWithBoolean('Header', item, comp.Header, isValidItems),
                Title: validateRepeatorFieldWithBoolean('Title', item, comp.Title, isValidItems),
                ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, comp.ImageUrl, isValidItems),
                Description: validateRepeatorFieldWithBoolean('Description', item, comp.Description, isValidItems),
                Items: (Array.isArray(comp.Items) && comp.Items.length > 0)
                  ? ''
                  : "At least one history item is required",
                Tabs: (Array.isArray(comp.Tabs) && comp.Tabs.length > 0)
                  ? comp.Tabs.map((tab: any) => ({
                      Title: validateRepeatorFieldWithBoolean('Title', item, tab.Title, isValidItems),
                      Description: validateRepeatorFieldWithBoolean('Description', item, tab.Description, isValidItems)
                    }))
                  : "At least one tab is required",
                Images: (Array.isArray(comp.Images) && comp.Images.length > 0)
                  ? ''
                  : "At least one partner image is required"
              }))
            : "At least one company detail section is required"
        };

      case WidgetType.BlogPost:
        return {
          Title: validateRepeatorFieldWithBoolean('Title', item, item.Title, isValidItems),
          Date: validateRepeatorFieldWithBoolean('Date', item, item.Date, isValidItems),
          ImageUrl: validateRepeatorFieldWithBoolean('ImageUrl', item, item.ImageUrl, isValidItems),
          Description: validateRepeatorFieldWithBoolean('Description', item, item.Description, isValidItems)
        };

      // For widgets that do not require specific field validation,
      // such as SidebarV1, BlogV1, and TestimonalV1, we return an empty object.
      case WidgetType.SidebarV1:
      case WidgetType.BlogV1:
        case WidgetType.BlogsListing:
      case WidgetType.TestimonalV1:
        return {};

      default:
        return {};
    }
  });

  const hasSeoErrors = Object.values(seoErrors).some(error => error !== '');
  const hasItemsErrors = itemsErrors.some(itemError => hasNestedErrors(itemError));
  const isValid = !(hasSeoErrors || hasItemsErrors);

  const errors = {
    SEO: seoErrors,
    Items: itemsErrors
  };

  const touched = {
    SEO: {
      Title: true,
      Description: true,
      Keywords: true,
      Code: true,
    },
    Items: pageData.Items.map(() => ({}))
  };

  return { errors, touched, isValid };
};
